/**
 * Copyright (C) 2016 Maxime Petazzoni <maxime.petazzoni@bulix.org>.
 * All rights reserved.
 * 
 * Slightly modified to parse SSE data line-by-line.
 */

var SSE = function(url, options) {
  if (!(this instanceof SSE)) {
    return new SSE(url, options);
  }

  // -- Constants --
  this.INITIALIZING = -1;
  this.CONNECTING = 0;
  this.OPEN = 1;
  this.CLOSED = 2;

  // -- Config --
  this.url = url;
  options = options || {};
  this.headers = options.headers || {};
  this.payload = options.payload !== undefined ? options.payload : '';
  this.method = options.method || (this.payload && 'POST' || 'GET');
  this.withCredentials = !!options.withCredentials;
  // Inactivity watchdog: if no bytes arrive for this long (ms), treat the
  // stream as stalled and surface a synthetic transient error so callers can
  // reconnect. 0 disables it. lastEventId tracks the SSE `id:` for resume.
  this.inactivityTimeoutMs = options.inactivityTimeoutMs || 0;
  this.lastEventId = '';
  this._watchdogTimer = null;

  // -- Internal fields --
  this.FIELD_SEPARATOR = ':';
  this.listeners = {};
  this.xhr = null;
  this.readyState = this.INITIALIZING;

  // These track streamed data:
  this.progress = 0;      // How many bytes we've processed so far
  this.buffer = '';       // Accumulate raw data here before splitting into lines
  this.eventLines = [];   // Accumulate lines for the current SSE event block

  // Provide basic trim polyfills for browsers missing trimLeft/trimRight (e.g. Safari)
  var trimEndPolyfill = function(str) {
    if (str == null) {
      return '';
    }
    if (typeof str.trimEnd === 'function') {
      return str.trimEnd();
    }
    if (typeof str.trimRight === 'function') {
      return str.trimRight();
    }
    return str.replace(/\s+$/, '');
  };

  var trimStartPolyfill = function(str) {
    if (str == null) {
      return '';
    }
    if (typeof str.trimStart === 'function') {
      return str.trimStart();
    }
    if (typeof str.trimLeft === 'function') {
      return str.trimLeft();
    }
    return str.replace(/^\s+/, '');
  };

  // -- Event handling methods --
  this.addEventListener = function(type, listener) {
    if (this.listeners[type] === undefined) {
      this.listeners[type] = [];
    }
    if (this.listeners[type].indexOf(listener) === -1) {
      this.listeners[type].push(listener);
    }
  };

  this.removeEventListener = function(type, listener) {
    if (this.listeners[type] === undefined) {
      return;
    }
    var filtered = [];
    this.listeners[type].forEach(function(element) {
      if (element !== listener) {
        filtered.push(element);
      }
    });
    if (filtered.length === 0) {
      delete this.listeners[type];
    } else {
      this.listeners[type] = filtered;
    }
  };

  this.dispatchEvent = function(e) {
    if (!e) {
      return true;
    }
    e.source = this;

    var onHandler = 'on' + e.type;
    if (this.hasOwnProperty(onHandler)) {
      this[onHandler].call(this, e);
      if (e.defaultPrevented) {
        return false;
      }
    }
    if (this.listeners[e.type]) {
      return this.listeners[e.type].every(function(callback) {
        callback(e);
        return !e.defaultPrevented;
      });
    }
    return true;
  };

  this._setReadyState = function(state) {
    var event = new CustomEvent('readystatechange');
    event.readyState = state;
    this.readyState = state;
    this.dispatchEvent(event);
  };

  // -- Inactivity watchdog --
  this._resetWatchdog = function() {
    if (!this.inactivityTimeoutMs) {
      return;
    }
    this._clearWatchdog();
    this._watchdogTimer = setTimeout(function() {
      // Silent for too long -> synthetic transient error (status 0). The stream
      // is closed; callers decide whether to reconnect from lastEventId.
      this._onStreamFailure(new CustomEvent('stall'), 0, 'stall_timeout');
    }.bind(this), this.inactivityTimeoutMs);
  };

  this._clearWatchdog = function() {
    if (this._watchdogTimer) {
      clearTimeout(this._watchdogTimer);
      this._watchdogTimer = null;
    }
  };

  // -- Error and abort handlers --
  this._onStreamFailure = function(e, status, responseText) {
    var event = new CustomEvent('error');
    event.data = {
      status: status,
      event: e,
      responseText: responseText,
    };
    this.dispatchEvent(event);
    this.close();
  };

  this._onStreamAbort = function(e) {
    this.dispatchEvent(new CustomEvent('abort'));
    this.close();
  };

  // -- Main streaming logic (line-by-line) --
  this._onStreamProgress = function(e) {
    if (!this.xhr) {
      return;
    }

    // Check status
    if (this.xhr.status !== 200) {
      this._onStreamFailure(e, this.xhr.status, this.xhr.responseText);
      return;
    }

    if (this.readyState === this.CONNECTING) {
      this.dispatchEvent(new CustomEvent('open'));
      this._setReadyState(this.OPEN);
    }

    // Bytes arrived -> the stream is alive; restart the inactivity watchdog.
    this._resetWatchdog();

    var newData = this.xhr.responseText.substring(this.progress);
    this.progress += newData.length;

    // Accumulate raw data into 'buffer'
    this.buffer += newData;
    var lines = this.buffer.split(/\r\n|\n|\r/);
    this.buffer = lines.pop(); // The last chunk might be incomplete

    lines.forEach(function(line) {
      if (!line.trim()) {
        // A blank line -> we've reached the end of the event block
        var chunk = this.eventLines.join('\n');
        this.eventLines = [];
        var evt = this._parseEventChunk(chunk);
        if (evt) {
          this.dispatchEvent(evt);
        }
      } else {
        this.eventLines.push(line);
      }
    }.bind(this));
  };

  this._onStreamLoaded = function(e) {
    this._onStreamProgress(e);

    if (this.eventLines.length > 0) {
      var chunk = this.eventLines.join('\n');
      this.eventLines = [];
      var evt = this._parseEventChunk(chunk);
      if (evt) {
        this.dispatchEvent(evt);
      }
    }
    this._checkStreamClosed();
  };

  this._checkStreamClosed = function() {
    if (!this.xhr) {
      return;
    }
    if (this.xhr.readyState === XMLHttpRequest.DONE) {
      this._clearWatchdog();
      this._setReadyState(this.CLOSED);
    }
  };

  /**
   * Parse a received SSE event chunk into a CustomEvent object.
   * This chunk is typically the concatenation of lines preceding a blank line.
   */
  this._parseEventChunk = function(chunk) {
    if (!chunk || chunk.length === 0) {
      return null;
    }

    var e = {
      id: null,
      retry: null,
      data: '',
      event: 'message', // default type is 'message'
    };

    // Split chunk by single newlines and parse
    chunk.split(/\r?\n/).forEach(function(line) {
      var trimmedLine = trimEndPolyfill(line);
      if (!trimmedLine) return; // skip empty/comment lines

      var index = trimmedLine.indexOf(this.FIELD_SEPARATOR);
      if (index <= 0) {
        // If no colon found (or starts with colon) -> comment or invalid line
        return;
      }

      var field = trimmedLine.substring(0, index).trim();
      var value = trimmedLine.substring(index + 1);
      value = trimStartPolyfill(value);

      // Recognize SSE fields
      if (field === 'data') {
        // data: lines can appear multiple times
        e.data += (e.data ? '\n' : '') + value;
      } else if (field === 'event') {
        e.event = value;
      } else if (field === 'id') {
        e.id = value;
      } else if (field === 'retry') {
        e.retry = value;
      }
      // ignore unknown fields
    }.bind(this));

    // Construct a CustomEvent from the final fields
    var evt = new CustomEvent(e.event);
    evt.data = e.data;
    evt.id = e.id;
    if (e.id !== null && e.id !== undefined && e.id !== '') {
      this.lastEventId = e.id;
    }
    return evt;
  };

  // -- Public API: start & close the connection --

  this.stream = function() {
    this._setReadyState(this.CONNECTING);

    this.xhr = new XMLHttpRequest();
    this.xhr.addEventListener('progress', this._onStreamProgress.bind(this));
    // Upload progress counts as activity too: with a large payload (várias
    // imagens em base64) o body leva mais que os 25s do watchdog para subir
    // e nenhum byte de RESPOSTA chega nesse período — sem este reset o
    // watchdog dispara stall_timeout e aborta o xhr NO MEIO do upload
    // (nginx loga 400 com request_time ~= timeout; mensagem nunca chega à API).
    this.xhr.upload.addEventListener('progress', this._resetWatchdog.bind(this));
    this.xhr.addEventListener('load', this._onStreamLoaded.bind(this));
    this.xhr.addEventListener('readystatechange', this._checkStreamClosed.bind(this));
    this.xhr.addEventListener('error', this._onStreamFailure.bind(this));
    this.xhr.addEventListener('abort', this._onStreamAbort.bind(this));
    this.xhr.open(this.method, this.url);
    for (var header in this.headers) {
      this.xhr.setRequestHeader(header, this.headers[header]);
    }
    this.xhr.withCredentials = this.withCredentials;
    this.xhr.send(this.payload);
    this._resetWatchdog();
  };

  this.close = function() {
    this._clearWatchdog();
    if (this.readyState === this.CLOSED) {
      return;
    }
    if (this.xhr) {
      this.xhr.abort();
      this.xhr = null;
    }
    this._setReadyState(this.CLOSED);
  };
};

if (typeof exports !== 'undefined') {
  exports.SSE = SSE;
}
